import { skipToken } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { Protocol, Record, RecordWithProtocol } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';


interface Props {
    record: Record | undefined;
    protocol: Protocol | undefined;
    limit?: number;
}

export function useFetchPrevRecords({
    record,
    protocol,
    limit,
}: Props) {


    const { data: protocols } = trpc.protocols.get.useQuery(
        protocol ? { partName: protocol.partName } : skipToken,
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
        }
    );

    const { data: batch, failureCount } = trpc.batches.getAtTime.useQuery(
        record && record.equipment && record.measurementStart ? {
            equipment: record.equipment,
            time: record.measurementStart,
        } : skipToken,
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
        }
    );

    const [hasBatchQueryFailed, setHasBatchQueryFailed] = useState(false);
    useEffect(() => {
        if (failureCount > 0) {
            setHasBatchQueryFailed(true);
        }
    }, [failureCount]);
    const batchFetchComplete = !!batch || hasBatchQueryFailed;

    const { data: recentRecordsFetch } = trpc.records.get.useQuery(
        record && protocols && record.measurementType === 'production' && batchFetchComplete ? {
            equipment: record.equipment!,
            protocolIdOptions: protocols.map(it => it._id!),
            measurementStart: { start: batch?.start, end: record.measurementStart },
            sort: { measurementStart: -1 },
            limit,
        } : skipToken,
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
        }
    );

    const { data: nextRecordFetch } = trpc.records.get.useQuery(
        record && protocols && record.measurementType === 'production' && batchFetchComplete ? {
            equipment: record.equipment!,
            protocolIdOptions: protocols.map(it => it._id!),
            measurementStart: { start: record.measurementStart, end: batch?.end ?? undefined },
            sort: { measurementStart: 1 },
            limit: 2,
        } : skipToken,
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
        }
    );

    const recentRecordsFinal = useMemo(() => {
        if (!protocols || !recentRecordsFetch) return undefined;
        const protocolMap = new Map(protocols.map(it => [it._id, it]));
        const sorted = sortBy([...recentRecordsFetch], it => it.measurementStart);
        return sorted.map(it => ({
            ...it,
            protocol: protocolMap.get(it.protocolId)!,
        })) as RecordWithProtocol[];
    }, [recentRecordsFetch, protocols]);


    return {
        prevRecord: recentRecordsFinal?.at(-1),
        nextRecord: nextRecordFetch?.[1],
        recentRecords: recentRecordsFinal,
    }
}
